import isEqual from 'lodash.isequal'
import { invariant } from './invariant'
import { EVENT_TYPE } from './event-type'

/**
 * Represents an analytics event.
 */
export class Event {
  /**
   * The type of the event.
   * @private
   * @readonly
   * @type {import("./event-type").EventType}
   */
  _type

  /**
   * The payload of the event.
   * @private
   * @readonly
   * @type {Record<string, string | number | boolean>}
   */
  _payload

  /**
   * Creates a new Event instance.
   * @param {import("./event-type").EventType} type - The type of the event.
   * @param {Record<string, string | number | boolean>} payload - The payload of the event.
   * @throws {Error} If the event type is invalid or the payload is null or undefined.
   */
  constructor(type, payload) {
    invariant(
      Object.values(EVENT_TYPE).includes(type),
      `Invariant Error: Invalid event type: ${type}`,
    )
    invariant(
      payload,
      `Invariant Error: Payload must be provided and cannot be null or undefined.`,
    )

    this._type = type
    this._payload = payload
  }

  /**
   * Gets the type of the event.
   * @returns {import("./event-type").EventType} The type of the event.
   */
  getType() {
    return this._type
  }

  /**
   * Sets a new payload for the event.
   * @param {Record<string, string | number | boolean>} payload - The new payload to set.
   */
  setPayload(payload) {
    this._payload = payload
  }

  /**
   * Gets the payload of the event.
   * @returns {Record<string, string | number | boolean>} The payload of the event.
   */
  getPayload() {
    return this._payload
  }

  /**
   * Checks if this event is equal to another event.
   * @param {Event} other - The other event to compare with.
   * @returns {boolean} True if the events are equal, false otherwise.
   */
  isEquals(other) {
    return this._type === other._type && isEqual(this._payload, other._payload)
  }
}
